import {stableStringify} from '@sail/utils';
import {buildQueryAndVariablesHash} from 'src/internal/revamp/queryUtils/queryAndVariablesHashing';

import type {WatchCleanupFn} from 'src/internal/revamp/cache/MemoryCache';
import type MemoryCache from 'src/internal/revamp/cache/MemoryCache';
import type {GraphQlOperationErrors} from 'src/internal/revamp/graphql/types/errors';
import type ErrorCache from 'src/internal/revamp/observable/ErrorCache';
import type {
  DataOfDoc,
  ErrorExtensionsOfDoc,
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';

type WatchResultsCallback<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> = (result: {
  data: DataOfDoc<TDocumentNode> | null;
  errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null;
}) => void;

type WatcherInfo<TDocumentNode extends GraphQlDocument<any, any, any, any>> = {
  query: TDocumentNode;
  variables: VariablesOfDoc<TDocumentNode> | undefined;
  callback: WatchResultsCallback<TDocumentNode>;
};

const DEFAULT_STALE_TIME = 30 * 1000;

export type StalenessState = 'STALE' | 'FRESH' | 'NOT_FOUND';

export default class ResultStore {
  private memoryCache: MemoryCache;
  private errorCache: ErrorCache;
  private queryStalenessMap: Map<string, number>;
  private watchers: Map<string, Set<WatcherInfo<any>>> = new Map();
  private staleTime: number;

  private isSettingResultsMap: Map<string, boolean> = new Map();

  constructor(
    memoryCache: MemoryCache,
    errorCache: ErrorCache,
    staleTime = DEFAULT_STALE_TIME,
  ) {
    this.memoryCache = memoryCache;
    this.errorCache = errorCache;
    this.queryStalenessMap = new Map();
    this.staleTime = staleTime;
  }

  public getStalenessState(hash: string): StalenessState {
    const staleTimestamp = this.queryStalenessMap.get(hash);

    if (typeof staleTimestamp === 'undefined') {
      return 'NOT_FOUND';
    }

    return Date.now() > staleTimestamp ? 'STALE' : 'FRESH';
  }

  private addStalenessTimestamp(
    hash: string,
    timestamp: number = Date.now(),
  ): void {
    this.queryStalenessMap.set(hash, timestamp + this.staleTime);
  }

  public invalidateQueriesByDocumentHash(hashes: ReadonlyArray<string>): void {
    for (const [cacheKey] of this.queryStalenessMap) {
      for (const hash of hashes) {
        if (cacheKey.startsWith(hash)) {
          this.queryStalenessMap.set(cacheKey, 0);
          break;
        }
      }
    }
  }

  public refreshStaleness<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
  ): void {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    this.addStalenessTimestamp(key);
  }

  public watchResults<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    callback: WatchResultsCallback<TDocumentNode>,
  ): WatchCleanupFn {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    const watcherInfo: WatcherInfo<TDocumentNode> = {
      query,
      variables,
      callback,
    };

    if (!this.watchers.has(key)) {
      this.watchers.set(key, new Set());
    }
    this.watchers.get(key)!.add(watcherInfo);

    let lastDataValue = this.getDataFromCache(query, variables);
    let lastErrorValue = this.getErrorsFromCache(query, variables);

    const notifyCallback = () => {
      // Don't notify if we're setting results, as it will cause a double render
      if (!this.isSettingResultsMap.get(key)) {
        callback({
          data: lastDataValue,
          errors: lastErrorValue,
        });
      }
    };

    const memoryCacheCleanup = this.memoryCache.watch({
      query,
      variables,
      callback: (data) => {
        lastDataValue = data;
        notifyCallback();
      },
    });

    const errorCacheCleanup = this.errorCache.observe(
      query,
      variables,
      (errors) => {
        lastErrorValue = errors;

        notifyCallback();
      },
    );

    return () => {
      memoryCacheCleanup();
      errorCacheCleanup();

      const watcherSet = this.watchers.get(key);

      if (watcherSet) {
        watcherSet.delete(watcherInfo);

        if (watcherSet.size === 0) {
          this.watchers.delete(key);
        }
      }
    };
  }

  public setFetchingResults<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: NoInfer<VariablesOfDoc<TDocumentNode>> | undefined,
    result: {
      data: NoInfer<DataOfDoc<TDocumentNode>> | null;
      errors: NoInfer<
        GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>>
      > | null;
    },
  ): void {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    const currentData = this.getDataFromCache(query, variables);
    const currentErrors = this.getErrorsFromCache(query, variables);

    // Set a lock to avoid double rerenders provoked by both errors and data updates
    this.isSettingResultsMap.set(key, true);

    if (result.data) {
      this.memoryCache.writeQuery({
        query,
        variables,
        data: result.data,
      });
    }

    if (result.errors) {
      this.errorCache.setErrors(query, variables, result.errors);
    } else {
      this.addStalenessTimestamp(key);

      this.errorCache.clearErrors(query, variables);
    }

    // Release a lock
    this.isSettingResultsMap.set(key, false);

    const dataAfterUpdate = this.getDataFromCache(query, variables);
    const errorsAfterUpdate = this.getErrorsFromCache(query, variables);

    const hasChanged =
      stableStringify(currentData) !== stableStringify(dataAfterUpdate) ||
      stableStringify(currentErrors) !== stableStringify(errorsAfterUpdate);

    // Always trigger callbacks when there are actual errors, even if the error hasn't changed.
    // This is necessary for retry logic - we need to process each fetch attempt's errors
    // to properly track retry counts and schedule subsequent retries.
    const hasActualErrors =
      errorsAfterUpdate !== null &&
      (errorsAfterUpdate.graphQlErrors.length > 0 ||
        errorsAfterUpdate.clientErrors.length > 0 ||
        errorsAfterUpdate.networkErrors.length > 0);
    const shouldNotify = hasChanged || hasActualErrors;

    if (shouldNotify) {
      const updatedResult = {data: dataAfterUpdate, errors: errorsAfterUpdate};
      this.triggerWatchCallbacks(query, variables, updatedResult);
    }
  }

  private getDataFromCache<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
  ): DataOfDoc<TDocumentNode> | null {
    return this.memoryCache.readQuery({
      query,
      variables,
    });
  }

  private getErrorsFromCache<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
  ): GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null {
    return this.errorCache.getErrors(query, variables);
  }

  public getCurrentResult<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
  ): {
    data: DataOfDoc<TDocumentNode> | null;
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null;
  } {
    return {
      data: this.getDataFromCache(query, variables),
      errors: this.getErrorsFromCache(query, variables),
    };
  }

  private triggerWatchCallbacks<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    result: {
      data: DataOfDoc<TDocumentNode> | null;
      errors: GraphQlOperationErrors<
        ErrorExtensionsOfDoc<TDocumentNode>
      > | null;
    },
  ): void {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    const watcherSet = this.watchers.get(key);

    if (watcherSet) {
      watcherSet.forEach((watcher) => {
        watcher.callback(result);
      });
    }
  }
}
